import { api, eflowApi } from "../../lib/api";
import { PropertiesForFiltering, RangeFilterType } from "../../swagger/caasService-v1";
import { formatDateWithTimezoneOffset } from "../../utils/dateFormatter";
export const getChargerDetails = async (chargerId, projectId, timezoneOffset) => {
    // Calculate today in the project's timezone
    const getCurrentDateInTimezone = () => {
        const now = new Date();
        if (!timezoneOffset)
            return now;
        // Extract offset hours from format like "UTC+01:00" or "+09:00"
        const offsetStr = timezoneOffset.replace("UTC", "");
        const offsetMatch = offsetStr.match(/([+-])(\d{1,2}):?(\d{2})?/);
        if (offsetMatch) {
            const sign = offsetMatch[1] === "+" ? 1 : -1;
            const hours = parseInt(offsetMatch[2], 10);
            const minutes = parseInt(offsetMatch[3] || "0", 10);
            const totalOffsetMinutes = sign * (hours * 60 + minutes);
            // Get UTC time and apply project timezone offset
            const utcTime = now.getTime() + now.getTimezoneOffset() * 60000;
            const projectTime = new Date(utcTime + totalOffsetMinutes * 60000);
            return projectTime;
        }
        return now;
    };
    // Helper to create a UTC date representing a specific time in the project timezone
    const createProjectDate = (year, month, day, hour = 0, minute = 0, second = 0, ms = 0) => {
        // Create date string in ISO format for the project timezone
        const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:${String(second).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
        // Parse as UTC so getUTC* methods return the correct values
        return new Date(dateStr + "Z");
    };
    const currentDate = getCurrentDateInTimezone();
    const currentDateYear = currentDate.getFullYear();
    const currentDateMonth = currentDate.getMonth();
    const currentDateDay = currentDate.getDate();
    // Get today's date in project's timezone (start of day 00:00:00)
    const startOfCurrentDate = createProjectDate(currentDateYear, currentDateMonth, currentDateDay, 0, 0, 0, 0);
    // Get end of today (23:59:59.999)
    const endOfCurrentDate = createProjectDate(currentDateYear, currentDateMonth, currentDateDay, 23, 59, 59, 999);
    const fromTime = formatDateWithTimezoneOffset(startOfCurrentDate, timezoneOffset);
    const toTime = formatDateWithTimezoneOffset(endOfCurrentDate, timezoneOffset);
    const { data } = await api.post(`/projects/${projectId}/chargers/search`, {
        filters: [
            {
                propertyName: "assetSourceId",
                type: "stringEquals",
                values: [chargerId]
            },
            {
                propertyName: "timestamp",
                range: {
                    includeLeft: true,
                    includeRight: false,
                    left: fromTime,
                    right: toTime
                },
                type: "timeRange"
            }
        ]
    });
    return data;
};
export const getChargerTransactions = async (assetId, projectId, queryParams) => {
    // Build filters array conditionally
    const filters = queryParams?.fromTime && queryParams?.toTime
        ? [
            {
                propertyName: "timestamp",
                range: {
                    includeLeft: true,
                    includeRight: false,
                    left: queryParams.fromTime,
                    right: queryParams.toTime
                },
                type: "timeRange"
            }
        ]
        : [];
    const params = new URLSearchParams();
    //add limit and skip params
    if (queryParams?.limit) {
        params.append("limit", queryParams.limit);
    }
    if (queryParams?.skip) {
        params.append("skip", queryParams.skip);
    }
    if (queryParams?.nextCursor) {
        params.append("cursor", queryParams.nextCursor);
    }
    if (queryParams?.sortBy) {
        params.append("sortBy", queryParams.sortBy);
        params.append("orderBy", queryParams.orderBy);
    }
    const { data } = await api.post(`/projects/${projectId}/chargers/${assetId}/transactions${params && "?" + params.toString()}`, {
        filters
    });
    return data;
};
export const getChargerTransactionsCount = async (assetId, projectId, queryParams) => {
    // Build filters array conditionally
    const filters = queryParams?.fromTime && queryParams?.toTime
        ? [
            {
                propertyName: "timestamp",
                range: {
                    includeLeft: true,
                    includeRight: false,
                    left: queryParams.fromTime,
                    right: queryParams.toTime
                },
                type: "timeRange"
            }
        ]
        : [];
    const { data } = await api.post(`/projects/${projectId}/chargers/${assetId}/transactions/COUNT`, {
        filters
    });
    return data;
};
export const getChargerHistory = async (assetId, projectId, queryParams) => {
    // Build filters array conditionally
    const filters = queryParams?.fromTime && queryParams?.toTime
        ? [
            {
                propertyName: "timestamp",
                range: {
                    includeLeft: true,
                    includeRight: false,
                    left: queryParams.fromTime,
                    right: queryParams.toTime
                },
                type: "timeRange"
            }
        ]
        : [];
    const { data } = await api.post(`/projects/${projectId}/chargers/${assetId}/status/history`, {
        filters
    });
    return data;
};
export const getChargerConnectorsEnergyHistogram = async (assetId, projectId, queryParams) => {
    // Build filters array conditionally
    const filters = queryParams?.fromTime && queryParams?.toTime
        ? [
            {
                propertyName: "timestamp",
                range: {
                    includeLeft: true,
                    includeRight: false,
                    left: queryParams.fromTime,
                    right: queryParams.toTime
                },
                type: "timeRange"
            }
        ]
        : [];
    const { data } = await api.post(`/projects/${projectId}/chargers/${assetId}/connectors/energy/histogram`, {
        filters
    });
    return data;
};
export const getChargerConnectorData = async (projectId, assetId, partyNumber) => {
    const { data } = await eflowApi.post("/assets/chargers/connectors", {
        assetId,
        partyNumber,
        projectId
    });
    return data;
};
export const getChargerTransactionList = async (chargerTransactionSearchRequest, queryParams) => {
    const allData = [];
    //default limit value is 20
    const limit = queryParams?.limit ? queryParams.limit : 20;
    // skip is offset value; callers may supply a starting offset instead of always paging from 0.
    let offset = queryParams?.skip ?? 0;
    const params = new URLSearchParams();
    if (queryParams?.sortBy) {
        params.append("sortBy", queryParams.sortBy);
        params.append("orderBy", queryParams.orderBy);
    }
    params.append("limit", limit.toString());
    params.append("skip", offset.toString());
    let nextCursor = undefined;
    // Tracks every cursor seen so far — a single "last cursor" check only catches an immediate
    // repeat and misses a multi-page cycle (e.g. A -> B -> A) looping us forever.
    const seenCursors = new Set();
    do {
        const { data } = await api.post(`/projects/${queryParams?.projectId}/chargers/${queryParams?.assetId}/transactions${params && "?" + params.toString()}`, chargerTransactionSearchRequest);
        nextCursor = data.responseMetadata?.nextCursor;
        if (nextCursor && seenCursors.has(nextCursor)) {
            throw new Error("Infinite loop error");
        }
        allData.push(data);
        if (nextCursor)
            seenCursors.add(nextCursor);
        params.set("cursor", nextCursor || "");
        // Preserve forward pagination. Resetting skip to 0 causes the Transactions API
        // to repeatedly return the same cursor and trigger the loop guard.
        offset += limit;
        params.set("skip", offset.toString());
    } while (nextCursor);
    return allData;
};
const ACTIVE_TRANSACTION_LOOKUP_DAYS = [1, 2, 5];
// Override the API default of 20: on a multi-connector charger a busy connector could otherwise fill
// the page and bury the other connector's active transaction.
const STOP_LOOKUP_PAGE_LIMIT = 50;
// Resolve "now" as a wall-clock time in the project timezone so the window aligns with local midnight
// rather than UTC midnight (which would drift the boundaries for non-UTC offsets/DST).
const getCurrentDateInTimezone = (timezoneOffset) => {
    const now = new Date();
    const offsetMatch = /([+-])(\d{1,2}):?(\d{2})?/.exec(timezoneOffset.replace("UTC", ""));
    if (!offsetMatch)
        return now;
    const sign = offsetMatch[1] === "+" ? 1 : -1;
    const hours = Number.parseInt(offsetMatch[2], 10);
    const minutes = Number.parseInt(offsetMatch[3] || "0", 10);
    const totalOffsetMinutes = sign * (hours * 60 + minutes);
    const utcTime = now.getTime() + now.getTimezoneOffset() * 60000;
    return new Date(utcTime + totalOffsetMinutes * 60000);
};
// Passing this TIMESTAMP range is what lets the backend return the active transaction — without it the
// Transactions API receives an empty filter and the Stop lookup fails.
const buildTransactionListTimeRangeFilter = (lookupDays, timezoneOffset) => {
    // A signless offset like "UTC" has no +/- for getCurrentDateInTimezone's regex to match, and
    // formatDateWithTimezoneOffset would strip "UTC" down to an empty suffix; normalize it to
    // +00:00 so the window calculation and the formatted timestamps agree on the same UTC offset.
    const normalizedOffset = /[+-]/.test(timezoneOffset) ? timezoneOffset : "+00:00";
    const currentDate = getCurrentDateInTimezone(normalizedOffset);
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const day = currentDate.getDate();
    // Derive non-overlapping windows while preserving the cumulative 1/2/5-day lookback.
    let windowStart = 3;
    if (lookupDays === 1)
        windowStart = 0;
    else if (lookupDays === 2)
        windowStart = 2;
    const windowEnd = lookupDays;
    // Date.UTC normalizes month/day underflow near the start of a month so
    // the window never becomes an Invalid Date; the resulting getUTC* fields equal the project wall-clock
    // time and formatDateWithTimezoneOffset re-stamps it with the project offset.
    const start = new Date(Date.UTC(year, month, day - windowEnd, 0, 0, 0, 0));
    // includeRight is false below, so the exclusive bound must be the next day's midnight — ending at
    // 23:59:59.999 would leave that exact instant outside every window.
    const end = new Date(Date.UTC(year, month, day - windowStart + 1, 0, 0, 0, 0));
    return {
        filters: [
            {
                propertyName: PropertiesForFiltering.TIMESTAMP,
                range: {
                    includeLeft: true,
                    includeRight: false,
                    left: formatDateWithTimezoneOffset(start, normalizedOffset),
                    right: formatDateWithTimezoneOffset(end, normalizedOffset)
                },
                type: RangeFilterType.TIME_RANGE
            }
        ]
    };
};
/**
 * Resolve the transaction the Stop command should target for a connector. A connector holds at most one
 * open transaction, so the active one is the most recent (DESC by startTime) transaction still open;
 * `getChargerTransactionList` pages through every cursor result so a busy connector can't bury it.
 */
export const getActiveTransactionId = async (assetId, projectId, connectorId, timezoneOffset) => {
    for (const lookupDays of ACTIVE_TRANSACTION_LOOKUP_DAYS) {
        const transactionList = await getChargerTransactionList(buildTransactionListTimeRangeFilter(lookupDays, timezoneOffset), {
            assetId,
            limit: STOP_LOOKUP_PAGE_LIMIT,
            orderBy: "DESC",
            projectId,
            skip: 0,
            sortBy: "startTime"
        });
        const transactionId = transactionList
            .flatMap((transactionPage) => transactionPage.transactions ?? [])
            .filter((transaction) => {
            // Skip closed transactions: any of these fields means the session already ended.
            if (transaction.endTime || transaction.stopActor || transaction.stopReason)
                return false;
            // Only the connector being stopped — never stop a session on the charger's other connector.
            if (!transaction.startTime || Number(transaction.connectorId) !== connectorId)
                return false;
            // Keep only rows with a valid startTime so we can reliably pick the latest one below.
            return Number.isFinite(Date.parse(transaction.startTime));
        })
            .sort((a, b) => Date.parse(b.startTime) - Date.parse(a.startTime))[0]?.transactionId;
        if (transactionId)
            return transactionId;
    }
    throw new Error(`Unable to stop charging. No active charging session was found for Connector ${connectorId}.`);
};
export const getProjectChargerFaultsList = async (params, chargerFaultSearchRequest) => {
    const queryParams = new URLSearchParams();
    //add limit and skip params
    if (params?.limit) {
        queryParams.append("limit", params.limit);
    }
    if (params?.skip) {
        queryParams.append("skip", params.skip);
    }
    if (params?.nextCursor) {
        queryParams.append("cursor", params.nextCursor);
    }
    if (params?.sortBy) {
        queryParams.append("sortBy", params.sortBy);
        queryParams.append("orderBy", params.orderBy);
    }
    const queryString = queryParams.toString();
    const { data } = await api.post(`/projects/${params.projectId}/chargers/${params.assetId}/faults${queryString ? "?" + queryString : ""}`, chargerFaultSearchRequest);
    return data;
};
export const getProjectChargerFaultsCount = async (params, chargerFaultCountRequest) => {
    const { data } = await api.post(`/projects/${params.projectId}/chargers/${params.assetId}/faults/COUNT`, chargerFaultCountRequest);
    return data;
};
